import { useContext, useEffect, useState, useRef } from "react";
import {
  VideoCameraIcon,
  KeyIcon,
  Bars3Icon,
  PowerIcon,
  TableCellsIcon,
  UserIcon,
  UserCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { BanknotesIcon } from "@heroicons/react/24/solid";
import { useNavigate, useLocation } from "react-router-dom";
import { ConfigContext } from "../util/ConfigContext";
import { ThemeContext } from "../util/ThemeContext";
import { apiGet } from "../util/axiosUtil";
import API_URL from "../config/apiConfig";
import ThemeToggle from "./ThemeToggle";
import ReusableActions from "./ReusableActions";
// import Marquee from "react-fast-marquee";
import { logoutWithRedirect } from "../util/JwtUtil";
import detectedPlatform, {
  getVersionDetailsIfApplicable,
} from "../util/PlatformUtils";
import { fakeData } from "../config/fakeUserConfig";
// import detectedPlatform from "../util/PlatformUtils";

const Header = ({ dashboardRef }) => {
  const { versionInfo, jwt, dialogRef } = useContext(ConfigContext);
  const [points, setPoints] = useState(null);
  const [isDashboardUser, setIsDashboardUser] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [headerMeta, setHeaderMeta] = useState(null);
  const [isFakeUser, setIsFakeUser] = useState(null);
  const dropdownRef = useRef(null);
  const themeData = useContext(ThemeContext);
  const appVersion = process.env.REACT_APP_VERSION;
  const navigate = useNavigate();
  const location = useLocation();

  const isDashboard = location.pathname === "/dashboard";
  const Icon = isDashboard ? VideoCameraIcon : TableCellsIcon;

  const handleSlideBar = () => {
    if (location.pathname === "/dashboard") {
      dashboardRef.current?.openSidebar?.();
    } else {
      dashboardRef.current?.openHomeSidebar?.();
    }
  };

  useEffect(() => {
    if (jwt?.un) {
      const isFake = fakeData.Users.includes(jwt?.un);
      setIsFakeUser(isFake);
    }
  }, [jwt]);

  const handleLogoClick = () => {
    if (isDashboardUser) {
      if (isDashboard) {
        navigate("/schedule");
      } else {
        navigate("/dashboard");
      }
    } else {
      navigate("/schedule");
    }
  };

  useEffect(() => {
    if (!jwt) return;

    const isAllowedRole = [
      "SUPER_MASTER",
      "MASTER",
      "OWNER",
      "ADMIN",
      "MANAGER",
    ].includes(jwt?.role);

    setIsDashboardUser(isAllowedRole);

    if (isAllowedRole) {
      const fetchPoints = async () => {
        try {
          const response = await apiGet(`${API_URL}/api/users/current-points`);
          setPoints(response.data?.data ?? 0);
        } catch (error) {
          console.error("Error fetching points:", error);
          setPoints(0); // Fallback value
        }
      };

      fetchPoints();
    } else {
      const fetchHeaderMeta = async () => {
        try {
          const response = await apiGet(
            `${API_URL}/api/subscribers/header-meta`,
          );
          setHeaderMeta(response.data?.data ?? null);
        } catch (error) {
          console.error("Error fetching header meta:", error);
          setHeaderMeta(null); // Fallback value
        }
      };
      if (jwt?.role === "SUBSCRIBER") fetchHeaderMeta();
    }
  }, [jwt]);

  const formatExpireDate = (rawDate) => {
    if (!rawDate || String(rawDate).length !== 8) return "N/A";

    const year = String(rawDate).substring(2, 4);
    const month = parseInt(String(rawDate).substring(4, 6), 10) - 1;
    const day = parseInt(String(rawDate).substring(6, 8), 10);

    const date = new Date(Date.UTC(2000 + parseInt(year, 10), month, day));

    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";

    const monthName = date.toLocaleString("default", { month: "short" });

    return `${day}${suffix} ${monthName} ${year}`;
  };

  const handleToggleNavigation = () => {
    const nextPath = isDashboard ? "/schedule" : "/dashboard";
    navigate(nextPath);
  };

  const toggleDialog = (action, data = null) => {
    dialogRef?.current?.updateChildState?.();
    dialogRef?.current?.updateCurrentAction?.(action);
    if (data) dialogRef?.current?.updateCurrentData?.(data);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* {jwt && !isFakeUser && (
        <Marquee
          gradient={false}
          speed={40}
          pauseOnHover={true}
          className="mt-0 z-50 bg-dashboardActiveTabBackgroundColor dark:bg-[#00ADB5] dark:text-gray-900 text-dashboardActiveTabTextAndIconColor font-medium py-1 px-4 text-sm sm:text-base md:text-lg "
        >
          😍 For Entertainment purpose only 🎉. Watch fastest streaming on the
          Internet. Subscribe now & enjoy.
        </Marquee>
      )} */}

      <header
        className={`w-full px-4 py-1.5 sm:py-2 z-10 transition-colors duration-300 ${
          location.pathname === "/" ||
          location.pathname === "/universal/login" ||
          location.pathname === "/auth/redirect"
            ? "bg-transparent border-none shadow-none"
            : "bg-[#FFFFFF] dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 shadow-sm shadow-gray-200 dark:shadow-gray-800"
        } ${
          location.pathname !== "/dashboard" &&
          location.pathname !== "/" &&
          location.pathname !== "/universal/login" &&
          location.pathname !== "/auth/redirect"
            ? "lg:px-44"
            : "lg:px-8"
        }`}
      >
        <div className="flex justify-between items-center w-full lg:px-8 z-0">
          {!isDashboard || !isLargeScreen ? (
            <div className="left-4 top-2 z-0">
              <button
                type="button"
                onClick={handleSlideBar}
                className="p-1 sm:p-2 text-zinc-50 dark:text-gray-400 bg-drawerButtonColor dark:bg-slate-700 rounded-md shadow-md hover:bg-drawerHoverButtonColor dark:hover:bg-gray-400 transition"
              >
                <Bars3Icon aria-hidden="true" className="h-6 w-6" />
              </button>
            </div>
          ) : null}

          {location.pathname !== "/" && (
            <div className="relative mt-1 mb-1 flex h-6 shrink-0 items-center z-0 pl-2">
              <img
                src={themeData.lightlogoUrl}
                alt="logo"
                onClick={handleLogoClick}
                className="justify-self-center h-10 w-10 sm:h-12 sm:w-12 mx-auto cursor-pointer"
                style={{ zIndex: 1000 }}
              />
            </div>
          )}

          <ReusableActions ref={dialogRef} />

          <div className="flex items-center gap-1 ml-auto z-0">
            <span
              className="font-semibold  transition-shadow duration-150
                    bg-gradient-to-r from-slate-100 via-slate-100 to-slate-300 border border-slate-200
                    dark:from-slate-900 dark:via-slate-900 dark:to-slate-500 dark:border-zinc-700 rounded px-1 py-0.5 sm:px-2 sm:py-1 text-[10px] sm:text-xs md:text-sm inline-block text-slate-700 dark:text-white"
            >
              {detectedPlatform() === "android"
                ? getVersionDetailsIfApplicable() + ` / v${appVersion}`
                : versionInfo
                  ? `v${versionInfo} / v${appVersion}`
                  : `v${appVersion}`}
            </span>

            {detectedPlatform() === "android" &&
              window?.ZoomHelper?.isAndroidTv?.() && (
                <button
                  onClick={() => window.location.reload()}
                  title="Refresh Page"
                  aria-label="Refresh Page"
                  className="p-2 rounded-full border border-gray-300 dark:border-gray-600 
                    bg-gray-100 dark:bg-gray-800 
                    hover:bg-gray-200 dark:hover:bg-gray-900 
                    focus:bg-blue-200 dark:focus:bg-blue-700 
                    focus:border-blue-400 dark:focus:border-blue-500 
                    text-gray-800 dark:text-gray-100 
                    transition-all duration-200 hover:scale-110 hover:shadow outline-none"
                >
                  <ArrowPathIcon className="w-5 h-5" />
                </button>
              )}

            {jwt && isDashboardUser && (
              <>
                {points !== null ? (
                  <div
                    className="inline-flex items-center gap-1 px-1 py-0.5 sm:px-2 sm:py-1 rounded shadow-sm transition-shadow duration-150
                    bg-gradient-to-r from-white via-green-100 to-green-300 border border-green-200
                    dark:from-slate-800 dark:via-slate-900 dark:to-yellow-800 dark:border-zinc-700"
                  >
                    <BanknotesIcon className="w-4 h-4 text-green-600 dark:text-yellow-300" />

                    <span className="hidden sm:inline text-[10px] sm:text-xs font-medium text-green-700 dark:text-white">
                      {points}
                    </span>

                    <span className="inline sm:hidden text-green-700 text-[10px] sm:text-xs font-medium dark:text-white">
                      {points > 999 ? Math.floor(points / 1000) + "K" : points}
                    </span>
                  </div>
                ) : (
                  <div
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full shadow-sm
                    bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-zinc-700 dark:via-zinc-800 dark:to-zinc-700
                    animate-pulse w-12 h-7"
                  >
                    <div className="w-3 h-3 rounded-full bg-green-400/50 dark:bg-yellow-300/30" />
                    <div className="h-2 rounded bg-green-500/30 dark:bg-white/20 flex-1" />
                  </div>
                )}

                <button
                  onClick={handleToggleNavigation}
                  title={isDashboard ? "Go to Schedule" : "Go to Dashboard"}
                  className="flex items-center justify-center p-1 sm:p-2 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-100/80 dark:bg-gray-800/80 text-gray-800 dark:text-white hover:bg-white dark:hover:bg-gray-700 ransition-all duration-200 hover:scale-110 hover:shadow"
                >
                  <Icon className="w-5 h-5" />
                </button>
              </>
            )}

            {location.pathname !== "/schedule" && <ThemeToggle />}

            {/* Round User Button with Dropdown */}
            {jwt && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center justify-center p-1 sm:p-2 rounded-full border border-gray-300 dark:border-gray-600 
                  bg-gray-100/80 dark:bg-gray-800/80 text-gray-800 dark:text-white 
                  hover:bg-white dark:hover:bg-gray-700 
                  focus:bg-blue-200 dark:focus:bg-blue-700 focus:border-blue-400 dark:focus:border-blue-500 
                  transition-all duration-200 hover:scale-110 hover:shadow outline-none"
                  title="User Info"
                >
                  <UserCircleIcon className="w-5 h-5" />
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-max rounded-xl shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-[100]">
                    <div className="p-2 text-sm text-left text-gray-800 dark:text-gray-100 space-y-2">
                      {/* User Info Section */}
                      <div className="flex items-center gap-3 p-2 bg-gray-100 dark:bg-gray-900 rounded-t-xl">
                        {/* Avatar */}
                        <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center">
                          <UserIcon className="w-6 h-6 text-white" />
                        </div>

                        {/* Info */}
                        <div>
                          <div className="flex items-center gap-1">
                            <p className="font-semibold text-sm text-gray-800 dark:text-white">
                              {jwt?.un}
                            </p>
                            {/* Badge */}
                            {!isDashboardUser && !isFakeUser && headerMeta && (
                              <span className="text-[10px] px-2 py-[2px] rounded-md bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300 font-semibold">
                                {headerMeta.daysLeft} Days
                              </span>
                            )}
                          </div>
                          {!isDashboardUser && !isFakeUser && headerMeta && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Expiry: {formatExpireDate(headerMeta.expireAt)}
                            </p>
                          )}
                        </div>
                      </div>

                      <hr className="my-2 border-gray-300 dark:border-gray-600" />

                      {/* Action Buttons */}
                      <div className="space-y-1">
                        {/* Reset Password */}
                        <button
                          onClick={() => {
                            setDropdownOpen(false);
                            toggleDialog("resetPassword");
                          }}
                          className="flex items-center w-full gap-2 px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 transition"
                        >
                          <KeyIcon className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                          Reset Password
                        </button>

                        {/* Go to Schedule */}
                        {isDashboardUser && (
                          <button
                            onClick={() => {
                              setDropdownOpen(false);
                              handleToggleNavigation();
                            }}
                            className="flex items-center w-full gap-2 px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 transition"
                          >
                            <Icon className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                            {isDashboard ? "Go to Schedule" : "Go to Dashboard"}
                          </button>
                        )}

                        {/* Logout */}
                        <button
                          onClick={() => {
                            setDropdownOpen(false);
                            logoutWithRedirect(navigate, jwt?.tenant);
                          }}
                          className="flex items-center w-full gap-2 px-3 py-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 transition"
                        >
                          <PowerIcon className="w-5 h-5 text-red-600" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="flex justify-center items-center">
        {jwt &&
          headerMeta &&
          !isFakeUser &&
          (() => {
            const daysLeft = headerMeta.daysLeft;

            const isExpired = daysLeft < 0;
            const isWarning = daysLeft >= 0 && daysLeft <= 3;

            const bgColor = isExpired
              ? "bg-red-600"
              : isWarning
                ? "bg-orange-500"
                : "bg-emerald-600";

            return (
              <div
                className={`
          w-full
          ${bgColor}
          text-white
          px-4
          py-2
          text-center
          font-semibold
          text-xs
          md:text-base
          tracking-wide
        `}
              >
                {isExpired ? (
                  <>
                    Hi <span className="font-bold">{jwt?.un}</span>, your ID
                    expired {Math.abs(daysLeft)} Day
                    {Math.abs(daysLeft) !== 1 ? "s" : ""} ago.
                  </>
                ) : (
                  <>
                    Hi <span className="font-bold">{jwt?.un}</span>, your ID
                    will expire in {daysLeft} Day{daysLeft !== 1 ? "s" : ""}.
                  </>
                )}
              </div>
            );
          })()}
      </div>
      {/* <div className="flex justify-center align-middle items-center">
        {jwt && headerMeta && !isFakeUser && (
          <div className="w-full shadow-sm shadow-gray-200 dark:shadow-gray-800 bg-black text-white font-semibold text-xs md:text-lg pt-1 pb-1">
            {headerMeta.daysLeft >= 0
              ? `💡 Hi ${jwt?.un}, Your ID will expire in: ${
                  headerMeta.daysLeft
                } Day${headerMeta.daysLeft > 1 ? "s" : ""} 💡`
              : `⚠️ Hi ${jwt?.un}, Your ID has expired. ⚠️`}
          </div>
        )}
      </div> */}
    </>
  );
};

export default Header;
